import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyFormation = () => {
  const { language } = useLanguage();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchSteps();
  }, []);

  const fetchSteps = async () => {
    try {
      const response = await axios.get(`${API}/guides/company-formation`);
      setSteps(response.data);
    } catch (error) {
      console.error('Error fetching steps:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="company-formation-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1676647018922-9b9e8ec7622d?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary/95"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {language === 'fr' ? 'Création d\'Entreprise' : 'Company Formation'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {language === 'fr' 
              ? 'Guide étape par étape pour créer votre entreprise en RDC'
              : 'Step-by-step guide to forming your company in DRC'}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="formation-steps">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-6">
            {steps.map((step, idx) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className={`bg-card border-2 p-8 transition-all cursor-pointer ${
                  activeStep === idx 
                    ? 'border-primary shadow-lg' 
                    : 'border-border/40 hover:border-primary/50'
                }`}
                onClick={() => setActiveStep(idx)}
                data-testid={`step-${step.step_number}`}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white font-serif text-2xl font-bold">
                        {step.step_number}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="font-serif text-2xl font-bold text-foreground">
                        {step.title}
                      </h3>
                      <div className="flex items-center space-x-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm font-mono">{step.estimated_time}</span>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {step.description}
                    </p>
                    
                    {activeStep === idx && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="border-t border-border pt-6 mt-6">
                          <div className="flex items-center space-x-2 mb-4">
                            <AlertCircle className="h-5 w-5 text-secondary" />
                            <h4 className="font-semibold text-foreground">
                              {language === 'fr' ? 'Documents Requis' : 'Required Documents'}
                            </h4>
                          </div>
                          <ul className="space-y-2">
                            {step.requirements.map((req, ridx) => (
                              <li key={ridx} className="flex items-start space-x-3">
                                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                <span className="text-muted-foreground">{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-white" data-testid="assistance-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl font-bold mb-6"
          >
            {language === 'fr' ? 'Besoin d\'Aide?' : 'Need Assistance?'}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg mb-8 opacity-90"
          >
            {language === 'fr' 
              ? 'Notre équipe peut vous accompagner à chaque étape du processus de création d\'entreprise.'
              : 'Our team can guide you through every step of the company formation process.'}
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            href="/contact"
            className="inline-block bg-secondary text-white px-8 py-4 text-lg font-serif hover:bg-secondary/90 transition-colors"
            data-testid="contact-assistance-btn"
          >
            {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
          </motion.a>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormation;